/**
 * Agent usage — per-agent RETAIL usage so a broker can recharge heavy agents
 * (brokerage_admin: own tenant; super_admin: any tenant via the picker).
 *
 * Reads GET /admin/billing/agent-costs. Retail numbers only — our internal AWS
 * cost per agent stays on the super-admin Margin view, never shown to brokers.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { adminService, billingService } from "@re-platform/api-client";
import type {
  AgentCostResponse,
  TenantWithPlan,
} from "@re-platform/api-client";
import { Card } from "@re-platform/ui";

import { api } from "../../lib/api";
import {
  bodyRow,
  tableShell,
  TableScroll,
  Td,
  Th,
  theadRow,
} from "../../lib/tables";
import { useRoles } from "../../lib/roles";

function money(v: string): string {
  const n = Number(v);
  return Number.isNaN(n) ? v : `$${n.toFixed(2)}`;
}

export function AgentUsage() {
  const roles = useRoles();
  const [tenantId, setTenantId] = useState("");

  const tenants = useQuery<TenantWithPlan[]>({
    queryKey: ["tenants"],
    queryFn: () => adminService.listTenants(api),
    staleTime: 30_000,
    enabled: roles.isSuperAdmin,
  });

  useEffect(() => {
    if (roles.isSuperAdmin && !tenantId && (tenants.data?.length ?? 0) > 0) {
      setTenantId(tenants.data![0]!.id);
    }
  }, [roles.isSuperAdmin, tenantId, tenants.data]);

  const tid = tenantId.trim() || undefined;
  const costs = useQuery<AgentCostResponse>({
    queryKey: ["agent-costs", tid ?? null],
    queryFn: () => billingService.getAgentCosts(api, { tenant_id: tid }),
    staleTime: 30_000,
    retry: false,
    // super_admin needs a tenant selected; brokerage_admin's is implied.
    enabled: !roles.isSuperAdmin || !!tid,
  });

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Agent usage</h1>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          marginBottom: 16,
        }}
      >
        Billable (retail) usage per agent for the last 30 days — use it to
        recharge agents who drive heavy usage.
      </p>

      {roles.isSuperAdmin && (
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 13,
            marginBottom: 16,
            maxWidth: 360,
          }}
        >
          <span style={{ color: "var(--rp-color-text-muted)" }}>Tenant</span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            style={{
              padding: "6px 8px",
              border: "1px solid var(--rp-color-border)",
              borderRadius: "var(--rp-radius-sm)",
              fontSize: 13,
            }}
          >
            <option value="">
              {tenants.isLoading ? "Loading tenants…" : "— Select a tenant —"}
            </option>
            {(tenants.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name} ({t.slug})
              </option>
            ))}
          </select>
        </label>
      )}

      {costs.data && (
        <p style={{ fontSize: 13, marginBottom: 12 }}>
          <strong>Total: {money(costs.data.total_usage)}</strong>
        </p>
      )}

      {costs.isError ? (
        <Card padding="lg">
          <p style={{ margin: 0, fontSize: 13 }}>Could not load agent usage.</p>
        </Card>
      ) : (
        <Card padding="none" style={{ overflow: "hidden" }}>
          <TableScroll>
            <table style={tableShell}>
              <thead>
                <tr style={theadRow}>
                  <Th>Agent</Th>
                  <Th>Email</Th>
                  <Th numeric>Events</Th>
                  <Th numeric>Usage (retail)</Th>
                </tr>
              </thead>
              <tbody>
                {(costs.data?.rows ?? []).map((r, i) => (
                  <tr key={r.user_id ?? `platform-${i}`} style={bodyRow}>
                    <Td bold>
                      {r.user_id === null
                        ? "Platform / system"
                        : (r.full_name ?? "—")}
                    </Td>
                    <Td muted>{r.email ?? "—"}</Td>
                    <Td numeric>{r.total_events}</Td>
                    <Td numeric bold>
                      {money(r.usage_cost)}
                    </Td>
                  </tr>
                ))}
                {costs.data && costs.data.rows.length === 0 && (
                  <tr style={bodyRow}>
                    <Td muted>No usage recorded yet.</Td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      )}
    </>
  );
}
